// src/features/product/ChipInput.tsx
// Free-text chip list editor for string[] fields — redirect URIs, scopes,
// resource types. Copied from features/booking/ChipInput (same pattern,
// kept domain-local per the form-kit convention rather than importing
// across feature dirs).

import { useState } from 'react';
import { useI18n } from '@react/hooks';

interface ChipInputProps {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  'aria-label'?: string;
}

export function ChipInput({ value, onChange, placeholder, 'aria-label': ariaLabel }: ChipInputProps) {
  const { t } = useI18n();
  const [draft, setDraft] = useState('');

  const commit = () => {
    const raw = draft.trim();
    if (!raw) return;
    const parts = raw
      .split(/[,\s]+/)
      .map((p) => p.trim())
      .filter((p) => p.length > 0 && !value.includes(p));
    if (parts.length > 0) onChange([...value, ...parts]);
    setDraft('');
  };

  const remove = (item: string) => onChange(value.filter((v) => v !== item));

  return (
    <div
      className="flex flex-wrap items-center gap-1.5 rounded-md border border-input bg-background p-2"
      role="group"
      aria-label={ariaLabel}
    >
      {value.map((item) => (
        <span
          key={item}
          className="inline-flex items-center gap-1 rounded-md bg-muted px-2 py-0.5 text-xs text-foreground font-mono"
        >
          {item}
          <button
            type="button"
            onClick={() => remove(item)}
            aria-label={t('common.remove')}
            className="text-muted-foreground hover:text-destructive"
          >
            <svg width="10" height="10" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
        </span>
      ))}
      <input
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ',') {
            e.preventDefault();
            commit();
          } else if (e.key === 'Backspace' && !draft && value.length > 0) {
            remove(value[value.length - 1]);
          }
        }}
        onBlur={commit}
        placeholder={value.length === 0 ? placeholder : undefined}
        aria-label={ariaLabel}
        className="min-w-[8rem] flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground font-mono"
      />
    </div>
  );
}

export default ChipInput;
